import { useMemo, useState } from 'react'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import Slider from '@/components/ui/Slider'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'

function monthsToPayoff(balance: number, monthlyRate: number, payment: number) {
  if (payment <= balance * monthlyRate) return { months: Infinity, totalInterest: Infinity }
  let bal = balance
  let months = 0
  let totalInterest = 0
  while (bal > 0.01 && months < 600) {
    const interest = bal * monthlyRate
    const principal = Math.min(bal, payment - interest)
    bal -= principal
    totalInterest += interest
    months += 1
  }
  return { months, totalInterest }
}

const faqItems = [
  { question: 'Is it better to pay off the highest interest or highest balance card first?', answer: 'The avalanche method (highest interest rate first) minimizes total interest paid. The snowball method (smallest balance first) builds psychological momentum, but usually costs more in interest.' },
  { question: 'How does the minimum payment trap work?', answer: 'Making only minimum payments extends payoff time for years and can multiply your total interest cost several times over, because minimum payments are calculated to barely exceed monthly interest charges.' },
  { question: 'Will paying more than the minimum hurt my credit score?', answer: 'No. Paying more than the minimum reduces your credit utilization ratio, which typically helps your credit score over time.' },
]

function CreditCardPayoff() {
  const [balance, setBalance] = useState(6500)
  const [apr, setApr] = useState(22.9)
  const [monthlyPayment, setMonthlyPayment] = useState(250)

  const results = useMemo(() => {
    const monthlyRate = apr / 100 / 12
    const minPayment = balance * 0.02 + balance * monthlyRate
    const withMin = monthsToPayoff(balance, monthlyRate, Math.max(minPayment, balance * 0.01))
    const withCustom = monthsToPayoff(balance, monthlyRate, monthlyPayment)
    const monthsSaved = Number.isFinite(withMin.months) && Number.isFinite(withCustom.months) ? withMin.months - withCustom.months : 0
    const interestSaved = Number.isFinite(withMin.totalInterest) && Number.isFinite(withCustom.totalInterest) ? withMin.totalInterest - withCustom.totalInterest : 0
    return { withMin, withCustom, monthsSaved, interestSaved, minPayment: Math.max(minPayment, balance * 0.01) }
  }, [balance, apr, monthlyPayment])

  return (
    <>
      <Seo
        title="Credit Card Payoff Calculator - Debt Snowball & Avalanche"
        description="Free credit card payoff calculator. See exactly how many months it takes to pay off your balance and how much interest you save with higher monthly payments."
        path="/calculators/credit-card-payoff"
        schema={[
          softwareApplicationSchema('Credit Card Payoff Calculator', 'Calculate credit card payoff time and interest savings.', 'https://www.mintcalculator.com/calculators/credit-card-payoff'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Credit Card Payoff Calculator', path: '/calculators/credit-card-payoff' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Credit Card Payoff Calculator', path: '/calculators/credit-card-payoff' }]} />
        <SectionHeading
          eyebrow="Loans, Credit & Debt Payoff"
          title="Credit Card Payoff Calculator"
          description="Compare minimum payments against a higher fixed payment to see how much time and interest you can save."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <NumberInput label="Current balance" value={balance} onChange={setBalance} prefix="$" step={100} />
            <Slider label="APR" value={apr} onChange={setApr} min={0} max={35} step={0.1} formatValue={v => `${v.toFixed(1)}%`} accent="amber" />
            <NumberInput label="Your monthly payment" value={monthlyPayment} onChange={setMonthlyPayment} prefix="$" step={10} accent="emerald" />
            <p className="text-xs text-slate-500">Estimated minimum payment: <span className="font-mono text-slate-300">{formatCurrency(results.minPayment)}</span></p>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:col-span-3">
            <StatCard
              label="Payoff Time"
              value={Number.isFinite(results.withCustom.months) ? `${results.withCustom.months} mo` : 'Never'}
              accent="emerald"
              size="lg"
            />
            <StatCard
              label="Total Interest"
              value={Number.isFinite(results.withCustom.totalInterest) ? formatCurrency(results.withCustom.totalInterest) : 'N/A'}
              accent="amber"
              size="lg"
            />
            <StatCard label="Months Saved vs Minimum" value={Number.isFinite(results.monthsSaved) ? `${Math.max(0, Math.round(results.monthsSaved))} mo` : 'N/A'} accent="teal" />
            <StatCard label="Interest Saved vs Minimum" value={Number.isFinite(results.interestSaved) ? formatCurrency(Math.max(0, results.interestSaved)) : 'N/A'} accent="cyan" />
          </div>
        </div>

        <div className="mt-6">
          <DisclaimerNote />
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default CreditCardPayoff
